import {
  sanitizeSupportFailureEvidence,
  SupportOperationFailure
} from './supportFailureEvidence'

/** Stable page identity. Route parameters never become participant-visible text. */
export interface SupportPageContext {
  version: 1
  pageKey: string
  routeTemplate: string | null
  productScope?: string
  failedOperation?: SupportOperationFailure
  affectedEntities?: import('./supportScreenContext').SupportEntitySuggestion[]
  relatedEntities?: import('./supportScreenContext').SupportEntitySuggestion[]
  relatedPromotions?: import('./supportScreenContext').SupportPromotionSuggestion[]
  relatedDistribution?: import('./supportScreenContext').SupportDistributionSuggestion[]
  relatedWalletTransactions?: import('./supportScreenContext').SupportWalletTransactionSuggestion[]
}

const promotionPages: ReadonlyArray<[RegExp, string, string]> = [
  [/^\/promotions\/?$/, 'promotion.overview', '/promotions'],
  [/^\/promotions\/list\/?$/, 'promotion.list', '/promotions/list'],
  [/^\/promotions\/create\/?$/, 'promotion.create', '/promotions/create'],
  [
    /^\/promotions\/evaluation-trace\/?$/,
    'promotion.evaluation',
    '/promotions/evaluation-trace'
  ],
  [
    /^\/promotions\/[^/]+\/vouchers\/?$/,
    'promotion.vouchers',
    '/promotions/:id/vouchers'
  ],
  [/^\/promotions\/[^/]+(?:\/edit)?\/?$/, 'promotion.detail', '/promotions/:id']
]

const entityPages: ReadonlyArray<[RegExp, string, string, string]> = [
  [
    /^\/adopay\/wallet\/[^/]+\/transaction-history(?:\/[^/]+)?\/?$/,
    'wallet.transaction',
    '/adopay/wallet/:walletId/transaction-history/:status?',
    'WALLET'
  ],
  [
    /^\/accommodations\/[^/]+\/[^/]+\/room-type\/[^/]+\/rate-plans\/[^/]+\/providerCode\/[^/]+\/[^/]+\/?$/,
    'rateplan.detail',
    '/accommodations/:id/:code/room-type/:room/rate-plans/:rate/providerCode/:provider/:page',
    'DISTRIBUTION'
  ],
  [
    /^\/accommodations\/[^/]+\/[^/]+\/[^/]+\/?$/,
    'accommodation.detail',
    '/accommodations/:id/:code/:page',
    'ASSET'
  ],
  [
    /^\/channels\/[^/]+\/[^/]+\/mapping\/?$/,
    'channel.mapping',
    '/channels/:id/:code/mapping',
    'DISTRIBUTION'
  ],
  [
    /^\/channels\/[^/]+\/[^/]+\/[^/]+\/?$/,
    'channel.detail',
    '/channels/:id/:code/:page',
    'ASSET'
  ],
  [
    /^\/provider\/[^/]+\/[^/]+\/[^/]+\/?$/,
    'provider.detail',
    '/provider/:id/:code/:page',
    'ASSET'
  ],
  [
    /^\/booking\/list\/[^/]+\/(?:view|edit|vouherPdf)(?:\/[^/]+)*\/?$/,
    'booking.detail',
    '/booking/list/:id/:page',
    'WBS'
  ],
  [
    /^\/access-management\/user-management\/user\/[^/]+(?:\/permissions)?\/?$/,
    'iam.user',
    '/access-management/user-management/user/:id',
    'IAM'
  ],
  [/^\/reports\/[^/]+\/?$/, 'reporting.overview', '/reports/:page', 'REPORTING']
]

export const SUPPORT_PAGE_LABELS: Readonly<Record<string, string>> = {
  'wallet.transaction': 'Transactions History',
  'promotion.overview': 'Promotion overview',
  'promotion.list': 'Promotion list',
  'promotion.create': 'New promotion',
  'promotion.detail': 'Promotion details',
  'promotion.vouchers': 'Promotion vouchers',
  'promotion.evaluation': 'Promotion evaluation',
  'accommodation.detail': 'Accommodation details',
  'channel.detail': 'Channel details',
  'provider.detail': 'Provider details',
  'channel.mapping': 'Channel mapping',
  'rateplan.detail': 'Rate plan details',
  'booking.detail': 'Booking details',
  'iam.user': 'User details',
  'reporting.overview': 'Reports'
}

export function supportPageContext(pathname: string): SupportPageContext {
  const path = pathname.split(/[?#]/, 1)[0].replace(/^\/(en|fa)(?=\/|$)/, '')
  if (!/[\s%\\]/.test(path)) {
    const entityPage = entityPages.find(([pattern]) => pattern.test(path))
    if (entityPage)
      return {
        version: 1,
        pageKey: entityPage[1],
        routeTemplate: entityPage[2],
        productScope: entityPage[3]
      }
    const page = promotionPages.find(([pattern]) => pattern.test(path))
    if (page) {
      return {
        version: 1,
        pageKey: page[1],
        routeTemplate: page[2],
        productScope: 'DISTRIBUTION'
      }
    }
  }
  return { version: 1, pageKey: 'unknown', routeTemplate: null }
}

export function sanitizeSupportEntities(value: unknown) {
  if (!Array.isArray(value)) return []
  return value
    .slice(0, 20)
    .filter(
      (
        entity
      ): entity is import('./supportScreenContext').SupportEntitySuggestion =>
        entity &&
        ['accommodation', 'channel', 'provider'].includes(entity.type) &&
        typeof entity.code === 'string' &&
        /^[A-Za-z0-9_.-]{1,512}$/.test(entity.code) &&
        typeof entity.label === 'string' &&
        entity.label.length <= 200
    )
    .map(({ type, code, label }) => ({ type, code, label }))
}

export function sanitizeSupportPromotions(value: unknown) {
  if (!Array.isArray(value)) return []
  return value
    .slice(0, 20)
    .filter(
      (
        item
      ): item is import('./supportScreenContext').SupportPromotionSuggestion =>
        item &&
        typeof item.id === 'string' &&
        /^[A-Za-z0-9_.-]{1,512}$/.test(item.id) &&
        typeof item.label === 'string' &&
        item.label.length <= 200
    )
    .map(({ id, label }) => ({ id, label }))
}

export function sanitizeSupportWalletTransactions(value: unknown) {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  return value
    .slice(0, 20)
    .filter((item): item is { id: string } => {
      if (
        !item ||
        typeof item.id !== 'string' ||
        !/^[A-Za-z0-9_.-]{1,512}$/.test(item.id) ||
        seen.has(item.id)
      )
        return false
      seen.add(item.id)
      return true
    })
    .map(({ id }) => ({ id }))
}

export function sanitizeSupportDistribution(value: unknown) {
  if (!Array.isArray(value)) return []
  const code = (item: unknown): item is string =>
    typeof item === 'string' && /^[A-Za-z0-9_.-]{1,512}$/.test(item)
  const result: import('./supportScreenContext').SupportDistributionSuggestion[] =
    []
  value.slice(0, 20).forEach((item) => {
    if (
      !item ||
      !code(item.propertyCode) ||
      typeof item.label !== 'string' ||
      item.label.length > 200
    )
      return
    const base = { propertyCode: item.propertyCode, label: item.label }
    if (
      item.refType === 'RATE_PLAN' &&
      code(item.roomTypeCode) &&
      code(item.ratePlanCode) &&
      item.channelCode === undefined
    ) {
      result.push({
        ...base,
        refType: 'RATE_PLAN',
        roomTypeCode: item.roomTypeCode,
        ratePlanCode: item.ratePlanCode
      })
    } else if (
      item.refType === 'CHANNEL_MAPPING' &&
      code(item.channelCode) &&
      item.roomTypeCode === undefined &&
      item.ratePlanCode === undefined
    ) {
      result.push({
        ...base,
        refType: 'CHANNEL_MAPPING',
        channelCode: item.channelCode
      })
    }
  })
  return result
}

/**
 * Every related-suggestion family, sanitized by its own validator. One place, so a
 * new family is added once rather than per page branch below.
 */
function relatedSuggestions(
  input: Partial<SupportPageContext>,
  pageKey: string
) {
  return {
    // A wallet reference is only ever raised from wallet history. Crafted router
    // state naming any other page does not get to carry one.
    ...(pageKey === 'wallet.transaction' && input.relatedWalletTransactions
      ? {
          relatedWalletTransactions: sanitizeSupportWalletTransactions(
            input.relatedWalletTransactions
          )
        }
      : {}),
    ...(input.relatedDistribution
      ? {
          relatedDistribution: sanitizeSupportDistribution(
            input.relatedDistribution
          )
        }
      : {}),
    ...(input.relatedPromotions
      ? {
          relatedPromotions: sanitizeSupportPromotions(input.relatedPromotions)
        }
      : {}),
    ...(input.relatedEntities
      ? { relatedEntities: sanitizeSupportEntities(input.relatedEntities) }
      : {}),
    ...(input.affectedEntities
      ? { affectedEntities: sanitizeSupportEntities(input.affectedEntities) }
      : {})
  }
}

/** Router state is caller-controlled: reconstruct display data from the registry. */
export function validatedSupportPageContext(
  value: unknown
): SupportPageContext | undefined {
  if (!value || typeof value !== 'object') return undefined
  const input = value as Partial<SupportPageContext>
  if (input.version !== 1 || typeof input.pageKey !== 'string') return undefined
  const failedOperation = sanitizeSupportFailureEvidence(input.failedOperation)
  const selectedFailure =
    failedOperation?.pageKey === input.pageKey ? { failedOperation } : {}
  const entityPage = entityPages.find((entry) => entry[1] === input.pageKey)
  if (entityPage)
    return {
      version: 1,
      pageKey: entityPage[1],
      routeTemplate: entityPage[2],
      productScope: entityPage[3],
      ...selectedFailure,
      ...relatedSuggestions(input, entityPage[1])
    }
  const page = promotionPages.find((entry) => entry[1] === input.pageKey)
  return page
    ? {
        version: 1,
        pageKey: page[1],
        routeTemplate: page[2],
        productScope: 'DISTRIBUTION',
        ...selectedFailure,
        ...relatedSuggestions(input, page[1])
      }
    : undefined
}
